import { MAX_STEPS_PER_FRAME, SIM_HZ } from '../data/tuning';

/**
 * Fixed-step loop with an accumulator (GDD §15.4). Pure: time only enters through the injected clock
 * and the `now` passed to `frame`, so the same timestamps always produce the same ticks.
 *
 * Ticks are derived from the time elapsed since the last rebase instead of subtracting 1000/60 ms over
 * and over, which is not representable in binary and would drift.
 */
export interface FixedStepLoopOptions {
  /** Current time in milliseconds. Read by `start()` and `resetClock()`. */
  now: () => number;
  /** Advances the simulation by one fixed step; `tick` is the monotonic counter after the step. */
  onStep: (tick: number) => void;
  /** Draws with the interpolation factor between the previous and the current state, in [0, 1). */
  onRender?: (alpha: number) => void;
  hz?: number;
  maxStepsPerFrame?: number;
}

const EPSILON_STEPS = 1e-9;

export class FixedStepLoop {
  /** Simulation steps run since creation. */
  tick = 0;
  /** Steps run by the last call to `frame`. */
  lastFrameSteps = 0;
  /** Interpolation factor of the last frame, in [0, 1). */
  alpha = 0;

  private readonly options: Required<Omit<FixedStepLoopOptions, 'onRender'>> & Pick<FixedStepLoopOptions, 'onRender'>;
  private running = false;
  private paused = false;
  private lastTime = 0;
  private baseTime = 0;
  private stepsSinceBase = 0;

  constructor(options: FixedStepLoopOptions) {
    this.options = { hz: SIM_HZ, maxStepsPerFrame: MAX_STEPS_PER_FRAME, ...options };
  }

  get isRunning(): boolean {
    return this.running;
  }

  get isPaused(): boolean {
    return this.paused;
  }

  /**
   * While paused, frames still render (with alpha frozen) so the pause dialog draws over the scene,
   * but no step runs. Resuming rebases the clock: the time spent paused is never simulated.
   */
  setPaused(paused: boolean): void {
    if (paused === this.paused) return;
    this.paused = paused;
    if (!paused) this.resetClock();
  }

  start(): void {
    this.running = true;
    this.resetClock();
  }

  stop(): void {
    this.running = false;
  }

  /** Starts measuring from now, so the next frame does not see the time spent paused or hidden. */
  resetClock(): void {
    const now = this.options.now();
    this.lastTime = now;
    this.baseTime = now;
    this.stepsSinceBase = 0;
    this.alpha = 0;
  }

  /** Runs the whole steps that fit in the time elapsed since the previous frame. Returns that count. */
  frame(now: number): number {
    this.lastFrameSteps = 0;
    if (!this.running || !Number.isFinite(now)) return 0;
    if (this.paused) {
      this.options.onRender?.(this.alpha);
      return 0;
    }

    const delta = now - this.lastTime;
    if (delta <= 0) {
      // A clock that goes backwards must not eat into the accumulated time: rebase on it.
      this.baseTime += delta;
      this.lastTime = now;
      return 0;
    }
    this.lastTime = now;

    const { hz, maxStepsPerFrame, onStep, onRender } = this.options;
    const exactSteps = ((now - this.baseTime) * hz) / 1000;
    const targetSteps = Math.floor(exactSteps + EPSILON_STEPS);
    let steps = targetSteps - this.stepsSinceBase;

    if (steps > maxStepsPerFrame) {
      // Spiral of death: run the cap and drop the pending time instead of queueing it.
      steps = maxStepsPerFrame;
      this.baseTime = now;
      this.stepsSinceBase = 0;
      this.alpha = 0;
    } else {
      this.stepsSinceBase = targetSteps;
      this.alpha = Math.min(Math.max(exactSteps - targetSteps, 0), 1 - Number.EPSILON);
    }

    for (let i = 0; i < steps && this.running; i++) {
      this.tick += 1;
      this.lastFrameSteps += 1;
      onStep(this.tick);
    }
    onRender?.(this.alpha);
    return this.lastFrameSteps;
  }
}
